import React, { useEffect, useMemo, useState } from 'react';
import { AuthService, AuthUser } from '../services/authService';
import RequestAccessForm from './RequestAccessForm';
import RedeemInviteForm from './RedeemInviteForm';
import AdminAccessPanel from './AdminAccessPanel';

type GateMode = 'login' | 'request' | 'redeem' | 'admin';

interface AuthGateProps {
  children: React.ReactNode;
}

export default function AuthGate({ children }: AuthGateProps) {
  const [statusChecked, setStatusChecked] = useState(false);
  const [requiresSetup, setRequiresSetup] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<GateMode>('login');
  const [adminOpen, setAdminOpen] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const unsubscribe = AuthService.onUnauthorized(({ reason }) => {
      if (cancelled) return;
      setUser(null);
      setError(reason === 'expired' ? 'Je sessie is verlopen. Log opnieuw in.' : '');
    });

    const unsubscribeSession = AuthService.onSessionChanged((nextUser) => {
      if (cancelled) return;
      setUser(nextUser);
    });

    const bootstrap = async () => {
      try {
        const oauthResult = AuthService.consumeOAuthRedirect();
        if (oauthResult.error) {
          setError(oauthResult.error);
        }
        if (oauthResult.user) {
          setUser(oauthResult.user);
        }

        const authStatus = await AuthService.getStatus();
        if (cancelled) return;

        setRequiresSetup(authStatus.requiresSetup && !oauthResult.user);
        setStatusChecked(true);

        const storedToken = AuthService.getToken();
        if (!storedToken) {
          setLoading(false);
          return;
        }

        const me = await AuthService.getCurrentUser();
        if (cancelled) return;

        setUser(me);
      } catch (bootstrapError: any) {
        if (cancelled) return;
        AuthService.clearSession();
        setError(bootstrapError?.message || 'Authenticatie initialiseren mislukt.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void bootstrap();

    return () => {
      cancelled = true;
      unsubscribe();
      unsubscribeSession();
    };
  }, []);

  const isSetupMode = statusChecked && requiresSetup && !user;
  const canSubmit = useMemo(() => {
    if (!email.trim() || !password.trim()) return false;
    if (isSetupMode) {
      return !!name.trim() && password === confirmPassword;
    }
    return true;
  }, [confirmPassword, email, isSetupMode, name, password]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError('');

    try {
      const nextUser = isSetupMode
        ? await AuthService.registerInitial(name.trim(), email.trim(), password)
        : await AuthService.login(email.trim(), password);

      setUser(nextUser);
      setRequiresSetup(false);
      setPassword('');
      setConfirmPassword('');
    } catch (submitError: any) {
      setError(submitError?.message || 'Authenticatie mislukt.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050816] text-white flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 rounded-3xl border border-white/10 bg-white/5 mx-auto animate-pulse" />
          <p className="text-sm font-bold uppercase tracking-[0.35em] text-slate-400">BDAT Secure Access</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="h-full w-full overflow-y-auto overscroll-contain bg-[radial-gradient(circle_at_top,_rgba(79,70,229,0.16),_transparent_38%),linear-gradient(180deg,#050816_0%,#0b1120_100%)] text-white custom-scrollbar">
        <div className="flex min-h-full items-start justify-center px-4 py-6 sm:px-6 sm:py-10">
        <div className="w-full max-w-5xl grid lg:grid-cols-[1.1fr_0.9fr] rounded-2xl sm:rounded-[2rem] overflow-hidden border border-white/10 bg-[#0b1020]/85 shadow-[0_30px_120px_rgba(2,6,23,0.7)] backdrop-blur-xl">
          <div className="p-6 sm:p-8 lg:p-14 border-b lg:border-b-0 lg:border-r border-white/10">
            <p className="text-[10px] font-black uppercase tracking-[0.45em] text-indigo-300">BDAT Autonomous</p>
            <h1 className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white">Beveiligde toegang tot je sales workspace</h1>
            <p className="mt-4 text-sm sm:text-base leading-7 sm:leading-8 text-slate-300 max-w-xl">
              {isSetupMode
                ? 'Maak eerst een beheeraccount aan voor deze VPS-installatie. Daarna is de volledige BDAT-omgeving alleen nog bereikbaar na login.'
                : 'Log in om projecten, transcripties, dossiers en agentworkflows te openen.'}
            </p>

            <div className="mt-6 sm:mt-10 hidden sm:grid gap-3 sm:gap-4 sm:grid-cols-2">
              {[
                'Projecten en dossiers achter een login',
                'Workspace-sync alleen voor ingelogde gebruiker',
                'Local LLM proxy en API achter JWT',
                'Klaar voor VPS-hosting met centrale toegang',
              ].map((item) => (
                <div key={item} className="rounded-[1.4rem] border border-white/10 bg-white/5 px-5 py-4 text-sm text-slate-200">
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 sm:p-8 lg:p-10">
            <div className="rounded-[1.3rem] sm:rounded-[1.8rem] border border-white/10 bg-[#0f172a]/80 p-5 sm:p-7">
              {mode === 'request' ? (
                <>
                  <p className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">Toegang aanvragen</p>
                  <h2 className="mt-3 text-2xl font-black">Vraag een uitnodiging aan</h2>
                  <p className="mt-3 text-sm leading-6 text-slate-400">
                    Vul je gegevens in. We mailen je zodra we je toegang hebben verleend.
                  </p>
                  <div className="mt-6">
                    <RequestAccessForm
                      onSwitchToRedeem={() => setMode('redeem')}
                      onSwitchToLogin={() => setMode('login')}
                      onBack={() => setMode('login')}
                    />
                  </div>
                </>
              ) : mode === 'redeem' ? (
                <>
                  <p className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">Uitnodigingscode</p>
                  <h2 className="mt-3 text-2xl font-black">Wissel je code in</h2>
                  <p className="mt-3 text-sm leading-6 text-slate-400">
                    Vul de code in die je van de beheerder hebt ontvangen.
                  </p>
                  <div className="mt-6">
                    <RedeemInviteForm
                      onApproved={() => setMode('login')}
                      onSwitchToRequest={() => setMode('request')}
                      onBack={() => setMode('login')}
                    />
                  </div>
                </>
              ) : (
                <>
              <p className="text-[10px] font-black uppercase tracking-[0.35em] text-slate-400">
                {isSetupMode ? 'Eerste Account' : 'Login'}
              </p>
              <h2 className="mt-3 text-2xl font-black">
                {isSetupMode ? 'Beheeraccount aanmaken' : 'Inloggen'}
              </h2>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => AuthService.beginOAuth('google')}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-white transition hover:border-white/25 hover:bg-white/10"
                >
                  Google login
                </button>
                <button
                  type="button"
                  onClick={() => AuthService.beginOAuth('github')}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-white transition hover:border-white/25 hover:bg-white/10"
                >
                  GitHub login
                </button>
              </div>

              {!isSetupMode && (
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setMode('request')}
                    className="rounded-2xl border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-amber-200 transition hover:border-amber-400/40 hover:bg-amber-500/10"
                  >
                    🔒 Toegang aanvragen
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('redeem')}
                    className="rounded-2xl border border-indigo-500/20 bg-indigo-500/5 px-3 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-indigo-200 transition hover:border-indigo-400/40 hover:bg-indigo-500/10"
                  >
                    🎟️ Code inwisselen
                  </button>
                </div>
              )}

              <div className="my-6 flex items-center gap-3 text-[10px] font-black uppercase tracking-[0.25em] text-slate-500">
                <div className="h-px flex-1 bg-white/10" />
                Of met wachtwoord
                <div className="h-px flex-1 bg-white/10" />
              </div>

              <form className="space-y-4" onSubmit={handleSubmit}>
                {isSetupMode && (
                  <label className="block">
                    <span className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-400">Naam</span>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-indigo-400"
                      placeholder="Jouw naam"
                    />
                  </label>
                )}

                <label className="block">
                  <span className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-400">E-mail</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-indigo-400"
                    placeholder="naam@bedrijf.nl"
                  />
                </label>

                <label className="block">
                  <span className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-400">Wachtwoord</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-indigo-400"
                    placeholder="Minimaal 8 tekens"
                  />
                </label>

                {isSetupMode && (
                  <label className="block">
                    <span className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-400">Herhaal Wachtwoord</span>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-indigo-400"
                      placeholder="Herhaal wachtwoord"
                    />
                  </label>
                )}

                {error && (
                  <div className="rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit || submitting}
                  className="w-full rounded-2xl bg-indigo-500 px-4 py-3 text-sm font-black uppercase tracking-[0.25em] text-white transition disabled:cursor-not-allowed disabled:opacity-50 hover:bg-indigo-400"
                >
                  {submitting ? 'Bezig...' : isSetupMode ? 'Account Aanmaken' : 'Inloggen'}
                </button>
              </form>
                </>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed top-[4.5rem] left-3 z-[125] lg:top-5 lg:left-[7.25rem] lg:z-[120] flex items-center gap-2 rounded-xl border border-white/10 bg-[#05070d]/88 px-2.5 py-1.5 shadow-2xl backdrop-blur-md">
        <div>
          <p className="text-[7px] font-black uppercase tracking-[0.22em] text-slate-500">In</p>
          <p className="text-[10px] leading-none font-black text-white truncate max-w-[82px]">{user.name}</p>
        </div>
        {user.role === 'ADMIN' && (
          <button
            onClick={() => setAdminOpen(true)}
            title="Toegangsbeheer"
            className="rounded-lg border border-amber-400/30 bg-amber-500/10 px-2 py-1.5 text-[8px] font-black uppercase tracking-[0.2em] text-amber-200 transition hover:border-amber-300/60 hover:bg-amber-500/20"
          >
            🔑 Admin
          </button>
        )}
        <button
          onClick={() => {
            AuthService.logout('logout');
            setUser(null);
          }}
          className="rounded-lg border border-white/10 px-2 py-1.5 text-[8px] font-black uppercase tracking-[0.2em] text-slate-200 transition hover:border-white/20 hover:bg-white/5"
        >
          Logout
        </button>
      </div>
      {adminOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-6" onClick={() => setAdminOpen(false)}>
          <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl border border-white/10 bg-[#0b1020]/95 p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.35em] text-amber-300">BDAT Toegangsbeheer</p>
                <h2 className="mt-2 text-2xl font-black text-white">Aanvragen & uitnodigingscodes</h2>
              </div>
              <button
                onClick={() => setAdminOpen(false)}
                className="rounded-xl border border-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-slate-300 hover:bg-white/5"
              >
                Sluiten
              </button>
            </div>
            <AdminAccessPanel />
          </div>
        </div>
      )}
      {children}
    </>
  );
}
